import { Link } from 'react-router-dom';
import { useIsAuthenticated } from '../stores/authStore';
import Button from '../components/ui/Button';
import { BookOpen, Mic, Gamepad2, Sparkles, Rocket, Heart } from 'lucide-react';

const FeatureCard = ({ icon, title, description, color }: { icon: React.ReactNode, title: string, description: string, color: string }) => (
    <div className="text-center p-8 bg-white rounded-2xl shadow-lg transform hover:scale-105 transition-transform duration-300">
        <div className={`flex items-center justify-center h-20 w-20 rounded-full ${color} text-white mx-auto mb-6 shadow-md`}>
            {icon}
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-3">{title}</h3>
        <p className="text-gray-600">{description}</p>
    </div>
);

const BenefitCard = ({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) => (
    <div className="bg-white rounded-2xl shadow-lg p-6 flex items-start space-x-4">
        <div className="flex-shrink-0">{icon}</div>
        <div>
            <h3 className="text-xl font-bold text-gray-800">{title}</h3>
            <p className="text-gray-600 mt-1">{description}</p>
        </div>
    </div>
);

export default function HomePage() {
    const isAuthenticated = useIsAuthenticated();

    return (
        <div className="min-h-screen bg-gradient-to-b from-yellow-50 via-orange-50 to-red-50 font-sans">
            {/* Header */}
            <header className="sticky top-0 bg-white/80 backdrop-blur-lg z-10 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <Link to="/" className="flex items-center space-x-2">
                            <img src="/logo.svg" alt="SayIt Logo" className="h-10 w-10" />
                            <h1 className="text-3xl font-bold text-purple-700">SayIt!</h1>
                        </Link>
                        <nav className="flex items-center space-x-4">
                            {isAuthenticated ? (
                                <Link to="/dashboard">
                                    <Button size="lg" className="rounded-full shadow-md hover:shadow-lg transition-shadow">Go to Dashboard</Button>
                                </Link>
                            ) : (
                                <div className="flex items-center space-x-3">
                                    <Link to="/login">
                                        <Button variant="secondary" size="lg" className="rounded-full">Sign In</Button>
                                    </Link>
                                    <Link to="/signup">
                                        <Button size="lg" className="rounded-full shadow-md hover:shadow-lg transition-shadow">Get Started</Button>
                                    </Link>
                                </div>
                            )}
                        </nav>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <div className="grid md:grid-cols-2 gap-12 items-center">
                    <div className="text-center md:text-left">
                        <h1 className="text-5xl md:text-7xl font-extrabold text-purple-800 tracking-tight mb-6">
                            Speak Clearly, <br />
                            <span className="text-orange-500">Shine Brightly!</span>
                        </h1>
                        <p className="text-xl text-gray-700 mb-10 max-w-2xl mx-auto md:mx-0">
                            Jump into a world of stories and games! Our friendly AI helps you speak new words like a star. Let's play and learn together!
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                            <Link to={isAuthenticated ? "/dashboard" : "/signup"}>
                                <Button size="xl" className="w-full sm:w-auto rounded-full shadow-xl hover:shadow-2xl transition-shadow transform hover:-translate-y-1">
                                    Start Your Adventure
                                </Button>
                            </Link>
                        </div>
                    </div>
                    <div className="flex justify-center">
                        <img src="/hero-illustration.svg" alt="Kids playing with letters" className="w-full max-w-md" />
                    </div>
                </div>
            </main>

            {/* Features Section */}
            <section className="py-20 bg-white/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-purple-800">How the Magic Happens</h2>
                        <p className="text-lg text-gray-600 mt-3">Learning to speak new words is a fun journey with SayIt!</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        <FeatureCard
                            icon={<BookOpen size={40} />}
                            title="Story Adventures"
                            description="Explore magical stories that make learning new words a blast."
                            color="bg-blue-500"
                        />
                        <FeatureCard
                            icon={<Mic size={40} />}
                            title="AI Talking Buddy"
                            description="Get super-friendly feedback on your words from our smart AI coach."
                            color="bg-green-500"
                        />
                        <FeatureCard
                            icon={<Gamepad2 size={40} />}
                            title="Fun & Games"
                            description="Play exciting games that help you remember words and have fun."
                            color="bg-yellow-500"
                        />
                    </div>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div className="flex justify-center">
                            <img src="/why-us-illustration.svg" alt="Happy child learning" className="w-full max-w-md" />
                        </div>
                        <div className="text-center md:text-left">
                            <h2 className="text-4xl font-bold text-purple-800 mb-8">Why You'll Love SayIt!</h2>
                            <div className="space-y-6">
                                <BenefitCard
                                    icon={<Sparkles className="w-10 h-10 text-yellow-500" />}
                                    title="Build Confidence"
                                    description="Feel proud and excited as you learn to speak more clearly every day."
                                />
                                <BenefitCard
                                    icon={<Rocket className="w-10 h-10 text-red-500" />}
                                    title="Learn Faster"
                                    description="Our AI helps you learn at your own pace, making it easier and quicker."
                                />
                                <BenefitCard
                                    icon={<Heart className="w-10 h-10 text-pink-500" />}
                                    title="Always Fun"
                                    description="With cool stories and games, learning never feels like homework."
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action Section */}
            <section className="py-20 bg-purple-600 text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-4xl font-bold mb-4">Ready to Start Your Word Adventure?</h2>
                    <p className="text-xl text-purple-200 mb-8">
                        Join thousands of happy learners and start speaking with confidence today!
                    </p>
                    <Link to={isAuthenticated ? "/dashboard" : "/signup"}>
                        <Button size="xl" variant="secondary" className="rounded-full shadow-lg hover:shadow-xl transition-shadow transform hover:scale-105">
                            Let's Go!
                        </Button>
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-800 text-white py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p>&copy; {new Date().getFullYear()} SayIt! All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
}